/**
 * AmountInput Component
 * Input for token amounts with max button and validation
 */

import { useState } from 'react'
import { Token } from '@contracts/tokens'
import { Button } from '@components/common/Button'
import { useTokenPrice, calculateFiatValue } from '@hooks/useTokenPrice'

interface AmountInputProps {
  value: string
  onChange: (value: string) => void
  token?: Token
  maxAmount?: bigint
  label?: string
  placeholder?: string
  showMaxButton?: boolean
  disabled?: boolean
  error?: string
}

export function AmountInput({
  value,
  onChange,
  token,
  maxAmount,
  label = 'Amount',
  placeholder = '0.00',
  showMaxButton = true,
  disabled = false,
  error,
}: AmountInputProps) {
  const [_isFocused, setIsFocused] = useState(false)
  
  // Fetch token price
  const { data: tokenPrice, isLoading: isPriceLoading } = useTokenPrice(token)
  
  // Calculate fiat value
  const fiatValue = token && tokenPrice ? calculateFiatValue(value, tokenPrice) : '0.00'

  const handleMaxClick = () => {
    if (!maxAmount || !token) return
    
    // Convert bigint to string with proper decimals
    const divisor = BigInt(10 ** token.decimals)
    const whole = maxAmount / divisor
    const fraction = maxAmount % divisor
    
    if (fraction === 0n) {
      onChange(whole.toString())
    } else {
      const fractionStr = fraction.toString().padStart(token.decimals, '0')
      const trimmed = fractionStr.replace(/0+$/, '')
      onChange(`${whole}.${trimmed}`)
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    
    // Allow empty string
    if (newValue === '') {
      onChange('')
      return
    }
    
    // Only allow numbers and one decimal point
    if (!/^\d*\.?\d*$/.test(newValue)) {
      return
    }
    
    // Limit decimal places based on token decimals
    if (token && newValue.includes('.')) {
      const [, decimals] = newValue.split('.')
      if (decimals && decimals.length > token.decimals) {
        return
      }
    }
    
    onChange(newValue)
  }

  // Calculate if amount exceeds max
  const exceedsMax = !!(token && maxAmount && value && value !== '' && (() => {
    try {
      const [whole = '0', fraction = ''] = value.split('.')
      const paddedFraction = fraction.padEnd(token.decimals, '0').slice(0, token.decimals)
      const combined = whole + paddedFraction
      const amountBigInt = BigInt(combined)
      return amountBigInt > maxAmount
    } catch {
      return false
    }
  })())

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {label}
        </label>
        {showMaxButton && !!maxAmount && token && (
          <Button
            size="sm"
            variant="ghost"
            onClick={handleMaxClick}
            disabled={disabled}
            className="text-xs"
          >
            Max
          </Button>
        )}
      </div>

      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={handleChange}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={placeholder}
          disabled={disabled}
          className={`
            w-full px-4 py-3 rounded-lg border
            ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white dark:bg-gray-800'}
            ${error || exceedsMax
              ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
              : 'border-gray-300 dark:border-gray-600 focus:ring-brand-orange focus:border-brand-orange'
            }
            focus:outline-none focus:ring-2
            text-gray-900 dark:text-white text-lg
          `}
        />
        
        {token && (
          <div className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400 font-medium pointer-events-none">
            {token.symbol}
          </div>
        )}
      </div>
      
      {/* Fiat value display */}
      {Boolean(token && value && !isPriceLoading && tokenPrice && parseFloat(value) > 0) && (
        <p className="text-sm text-gray-600 dark:text-gray-400">
          ≈ ${fiatValue} USD
        </p>
      )}

      {exceedsMax && (
        <p className="text-sm text-red-600 dark:text-red-400">
          Amount exceeds available balance
        </p>
      )}

      {error && !exceedsMax && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {token && !!maxAmount && !error && !exceedsMax && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Available:{' '}
          <button
            type="button"
            onClick={handleMaxClick}
            disabled={disabled}
            className="font-semibold text-brand-orange hover:text-brand-orange/80 underline cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {formatMaxAmount(maxAmount, token.decimals)} {token.symbol}
          </button>
        </p>
      )}
    </div>
  )
}

/**
 * Format max amount for display
 */
function formatMaxAmount(amount: bigint, decimals: number): string {
  const divisor = BigInt(10 ** decimals)
  const whole = amount / divisor
  const fraction = amount % divisor
  
  if (fraction === 0n) {
    return whole.toString()
  }
  
  const fractionStr = fraction.toString().padStart(decimals, '0')
  const trimmed = fractionStr.replace(/0+$/, '')
  const limited = trimmed.slice(0, 4) // Show max 4 decimal places
  
  return `${whole}.${limited}`
}

